import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  TrainerAchievementCooldownResponse,
  TrainerAchievementResponse,
  TrainerExerciseDetailsResponseMastery,
  TrainerService,
} from '@thiememeulenhoff/thor-client';
import { firstValueFrom } from 'rxjs';
import {
  toRequestError,
  withRequestStatus,
} from '../_utils/request-status.feature';

type MasteryState = {
  mastery: TrainerExerciseDetailsResponseMastery;
  achievements: Array<TrainerAchievementResponse>;
  achievementCooldown: TrainerAchievementCooldownResponse | undefined;
};

const initialState: MasteryState = {
  mastery: {},
  achievements: [],
  achievementCooldown: undefined,
};

export const MasteryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withComputed(({ achievements }) => ({
    badgeCount: computed(() => achievements().length),
  })),
  withMethods((store, trainerService = inject(TrainerService)) => ({
    async fetchMasteryProgress(
      arrangementCode: string,
      wordListId: string,
    ): Promise<void> {
      patchState(store, {
        mastery: initialState.mastery,
        achievements: initialState.achievements,
        achievementCooldown: initialState.achievementCooldown,
        requestStatus: 'loading',
      });
      try {
        const exercise = await firstValueFrom(
          trainerService.getTrainerExercise('wordlist', wordListId, {
            arrangementCode,
          }),
        );
        patchState(store, {
          mastery: exercise.mastery ?? initialState.mastery,
          achievements: exercise.achievements,
          achievementCooldown: exercise.achievementCooldown,
          requestStatus: 'success',
        });
      } catch (error) {
        patchState(store, { requestStatus: toRequestError(error) });
        throw error;
      }
    },
    async refreshAchievementCooldown(
      arrangementCode: string,
      wordListId: string,
    ): Promise<void> {
      try {
        const exercise = await firstValueFrom(
          trainerService.getTrainerExercise('wordlist', wordListId, {
            arrangementCode,
          }),
        );
        patchState(store, {
          achievementCooldown: exercise.achievementCooldown,
        });
      } catch {
        // Keep previous cooldown and don't error the entire session.
      }
    },
  })),
);
